import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/useAppStore';
import { AudioGenerationService } from '@/application/services/AudioGenerationService';
import { SceneService } from '@/application/services/SceneService';
import { errorMessages } from '@/lib/errorMessages';
import { logger } from '@/lib/logger';

export function useAudioController() {
  const {
    scenes,
    settings,
    updateScene,
    deleteSceneAudio,
    setGenerationStatus,
    resetGeneration,
    showCompletionDialog,
    showErrorConfirmDialog,
    registerCancelCallback,
  } = useAppStore();

  const audioService = useMemo(() => new AudioGenerationService(), []);
  const sceneService = useMemo(() => new SceneService(), []);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);

  // コンポーネントアンマウント時にWorkerを終了（メモリリーク対策）
  useEffect(() => {
    return () => {
      audioService.terminateWorker();
    };
  }, [audioService]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudioId(null);
  }, []);

  const handlePlayAudio = useCallback((audioUrl: string, sceneId: string) => {
    // 同じシーンの音声が再生中なら停止
    if (playingAudioId === sceneId && audioRef.current) {
      stopAudio();
      return;
    }

    // 別の音声が再生中なら先に停止
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(audioUrl);
    // 遅延して発火した古いAudioのイベントが新しい再生の状態を消さないよう、
    // 自分がまだ現在の再生対象かを確認してから状態を戻す
    const resetIfCurrent = () => {
      if (audioRef.current === audio) {
        audioRef.current = null;
        setPlayingAudioId(null);
      }
    };
    audio.onended = resetIfCurrent;
    audio.onerror = () => {
      logger.error('音声再生中にエラーが発生しました', new Error('Audio playback error'), {
        audioUrl: audioUrl.substring(0, 50) + '...',
      });
      resetIfCurrent();
    };
    audioRef.current = audio;
    setPlayingAudioId(sceneId);
    audio.play().catch((error) => {
      logger.error('音声再生に失敗しました', error as Error, {
        audioUrl: audioUrl.substring(0, 50) + '...',
      });
      resetIfCurrent();
    });
  }, [playingAudioId, stopAudio]);

  const handleDeleteAudio = useCallback(
    (sceneId: string) => {
      const scene = scenes.find((s) => s.id === sceneId);
      if (!scene) return;

      // 再生中の音声なら停止
      if (playingAudioId === sceneId) {
        stopAudio();
      }

      deleteSceneAudio(sceneId);
      toast.success('音声を削除しました');
    },
    [scenes, deleteSceneAudio, playingAudioId, stopAudio]
  );

  const handleRegenerateAudio = useCallback(
    async (sceneId: string) => {
      if (!settings.elevenLabsApiKey || !settings.voiceId) {
        const errMsg = errorMessages.apiKeyMissing();
        toast.error(errMsg.title, { description: errMsg.message });
        return;
      }

      const scene = scenes.find((s) => s.id === sceneId);
      if (!scene || !scene.text) return;

      logger.info('個別音声再生成を開始', { sceneId });

      updateScene(sceneId, { audioStatus: 'generating' });

      try {
        const result = await audioService.generateSingleAudio(scene, settings);
        updateScene(sceneId, {
          audioStatus: 'done',
          audioUrl: result.audioUrl,
          audioDuration: result.audioDuration,
        });
        logger.info('個別音声再生成成功', {
          sceneId,
          duration: result.audioDuration,
        });
      } catch (error) {
        logger.error('個別音声再生成失敗', error as Error, { sceneId });
        updateScene(sceneId, { audioStatus: 'error' });
        const errMsg = errorMessages.audioGenerationFailed(error as Error);
        toast.error(errMsg.title, { description: errMsg.message });
      }
    },
    [settings, scenes, audioService, updateScene]
  );

  const handleUploadAudio = useCallback(
    async (sceneId: string, file: File) => {
      logger.info('音声ファイルアップロード開始', {
        sceneId,
        fileName: file.name,
        fileSize: file.size,
      });

      try {
        const result = await audioService.uploadAudioFile(file);
        updateScene(sceneId, {
          audioStatus: 'done',
          audioUrl: result.audioUrl,
          audioDuration: result.audioDuration,
        });
        logger.info('音声ファイルアップロード成功', {
          sceneId,
          duration: result.audioDuration,
        });
      } catch (error) {
        logger.error('音声ファイルアップロード失敗', error as Error, { sceneId });
        const errMsg = errorMessages.audioUploadFailed(error as Error);
        toast.error(errMsg.title, { description: errMsg.message });
      }
    },
    [audioService, updateScene]
  );

  const handleDownloadAudio = useCallback(
    (sceneId: string) => {
      const scene = scenes.find((s) => s.id === sceneId);
      if (!scene || !scene.audioUrl) return;

      audioService.downloadAudio(scene.audioUrl, `scene_${scene.order + 1}_audio.mp3`);
    },
    [scenes, audioService]
  );

  const handleDownloadAllAudio = useCallback(async () => {
    const sortedScenes = sceneService.getSortedScenes(scenes);
    try {
      await audioService.downloadAllAudios(sortedScenes);
    } catch (error) {
      toast.error('ダウンロードエラー', { description: (error as Error).message });
    }
  }, [scenes, audioService, sceneService]);

  const handleGenerateAllAudio = useCallback(
    async (onComplete?: () => void) => {
      if (!settings.elevenLabsApiKey || !settings.voiceId) {
        const errMsg = errorMessages.apiKeyMissing();
        toast.error(errMsg.title, { description: errMsg.message });
        return;
      }

      const sortedScenes = sceneService.getSortedScenes(scenes);
      const scenesToGenerate = audioService.filterScenesForGeneration(sortedScenes);

      if (scenesToGenerate.length === 0) {
        logger.warn('生成するシーンがありません');
        toast.error('生成するシーンがありません', { description: 'すでにすべてのシーンに音声が設定されています。' });
        return;
      }

      logger.info('音声のみ生成を開始（Worker版）', { sceneCount: scenesToGenerate.length });

      // キャンセル処理を登録（Worker版はcancelWorkerGenerationを使用）
      registerCancelCallback(() => {
        audioService.cancelWorkerGeneration();
        logger.info('音声生成がユーザーによってキャンセルされました');
      });

      setGenerationStatus({
        isGenerating: true,
        currentStep: 'audio',
        progress: 0,
        currentSceneIndex: 0,
        totalScenes: scenesToGenerate.length,
        message: '音声生成を開始...',
      });

      const result = await audioService.generateBatchAudioWithWorker(
        scenesToGenerate,
        settings,
        {
          onSceneStart: (id) => updateScene(id, { audioStatus: 'generating' }),
          onSceneComplete: (id, data) =>
            updateScene(id, {
              audioStatus: 'done',
              audioUrl: data.audioUrl,
              audioDuration: data.audioDuration,
            }),
          onProgress: (current, total, message) => {
            setGenerationStatus({
              currentSceneIndex: current,
              progress: Math.round((current / total) * 100),
              message,
            });
          },
          onError: async (error, sceneId) => {
            const scene = scenes.find((s) => s.id === sceneId);
            const sceneInfo = scene ? `シーン${scene.order + 1}: ` : '';
            const errMsg = errorMessages.audioGenerationFailed(error);
            return showErrorConfirmDialog(
              errMsg.title,
              `${sceneInfo}${errMsg.message}\n\n残りのシーンの処理を続行しますか？`
            );
          },
        }
      );

      // キャンセルコールバックをクリア
      registerCancelCallback(null);

      if (result.success) {
        setGenerationStatus({
          isGenerating: false,
          currentStep: 'done',
          progress: 100,
          message: '音声生成完了！',
        });
        logger.info('全音声生成完了', { totalScenes: scenesToGenerate.length });
        resetGeneration();
        showCompletionDialog('音声生成完了', 'すべての音声の生成が正常に完了しました。');
        onComplete?.();
      } else {
        setGenerationStatus({
          isGenerating: false,
          currentStep: 'error',
          message: '音声生成を中断しました',
        });
        resetGeneration();
      }
    },
    [
      settings,
      scenes,
      audioService,
      sceneService,
      updateScene,
      setGenerationStatus,
      resetGeneration,
      showCompletionDialog,
      showErrorConfirmDialog,
      registerCancelCallback,
    ]
  );

  return {
    handlePlayAudio,
    handleDeleteAudio,
    handleRegenerateAudio,
    handleUploadAudio,
    handleDownloadAudio,
    handleDownloadAllAudio,
    handleGenerateAllAudio,
    cleanupAudio: stopAudio,
    playingAudioId,
  };
}
